import * as React from "react";
import { Layout, PageHeader, Spin, Row, Col } from "antd";

import { classname } from "@ctra/utils";

import { ContentWrapper } from "../../layout";
import ChartHeader, { ChartHeaderProps } from "../ChartHeader/ChartHeader";
import styles from "./Chart.module.less";

const { Content } = Layout;

export enum ChartVariant {
  /**
   * "headless" chart displaying only the graph
   * @type {ChartVariant.Embedded}
   */
  Embedded = "embedded",
  /**
   * full UI with header
   * @type {ChartVariant.Standalone}
   */
  Standalone = "standalone",
  /**
   * V2 styling
   * @type {ChartVariant.V2}
   */
  V2 = "v2",
  /**
   * V3 stylinh
   */
  V3 = "v3"
}

export interface ChartProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">, ChartHeaderProps {
  /**
   * Chart variant
   */
  variant?: ChartVariant;
  /**
   * Chart filtering widgets
   */
  filters?: React.ReactNode;
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Loading text
   */
  tip?: string;
  /**
   * extra content for the chart header
   */
  extra?: React.ComponentProps<typeof PageHeader>["extra"];
  /**
   * extra content for the chart header
   */
  extension?: React.ReactNode;
  /**
   * Chart footer
   */
  footer?: React.ReactNode;
  /**
   * custom chart tag
   */
  tag?: JSX.Element;
}

/**
 * Chart container which encapsulates the Mongo Charts iframe.
 * @param {boolean | undefined} loading
 * @param {string | undefined} tip
 * @param {string | React.ReactElement<any, string | React.JSXElementConstructor<any>> | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} title
 * @param {string | undefined} subtitle
 * @param {string | undefined} description
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} filters
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined | (React.ReactElement<any, string | React.JSXElementConstructor<any>> & undefined) | (Iterable<React.ReactNode> & undefined) | (React.ReactPortal & undefined) | (Iterable<ReactI18NextChild> & React.ReactElement<any, string | React.JSXElementConstructor<any>>) | (Iterable<ReactI18NextChild> & string) | (Iterable<ReactI18NextChild> & number) | Iterable<ReactI18NextChild> | (Iterable<ReactI18NextChild> & Iterable<React.ReactNode>) | (Iterable<ReactI18NextChild> & React.ReactPortal) | (Iterable<ReactI18NextChild> & boolean) | (Iterable<ReactI18NextChild> & null) | (Iterable<ReactI18NextChild> & undefined)} children
 * @param {string | undefined} className
 * @param {ChartVariant | undefined} variant
 * @param {JSX.Element | undefined} tag
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} extra
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} extension
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} footer
 * @param {Pick<ChartProps & {children?: React.ReactNode | undefined}, "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture">} rest
 * @return {JSX.Element}
 * @constructor
 */
const Chart: React.FC<ChartProps> = ({
  loading = false,
  tip,
  title,
  subtitle,
  description,
  filters,
  children,
  className = "",
  variant = ChartVariant.Standalone,
  tag,
  extra,
  extension,
  footer = null,
  ...rest
}) => {
  /**
   * Tell if the chart should display a header
   */
  const rendersHeader = [ChartVariant.Standalone, ChartVariant.V2].includes(variant) && title;

  return (
    <Layout
      className={classname(
        "ctra-chart-wrapper",
        styles.Chart,
        variant === ChartVariant.Standalone ? styles.Standalone : null,
        variant === ChartVariant.V2 ? styles.V2 : null,
        className
      )}
      {...rest}
    >
      <Spin spinning={loading} tip={tip}>
        {rendersHeader && (
          <ChartHeader title={title} subtitle={subtitle} description={description} extra={extra} tag={tag} />
        )}
        {variant === ChartVariant.V3 && (
          <Col className={styles.Header}>
            <ChartHeader extra={extra} />
          </Col>
        )}
        {filters && <PageHeader className={styles.Filter}>{filters}</PageHeader>}
        <Row gutter={[0, 16]} className={styles.Content}>
          <Col span={24} lg={extension ? 18 : 24}>
            {variant === ChartVariant.V2 ? (
              <ContentWrapper className="ctra-chart-content">{children}</ContentWrapper>
            ) : (
              <Content className="ctra-chart-content">{children}</Content>
            )}
          </Col>
          {extension && (
            <Col span={24} lg={6}>
              <ContentWrapper className="ctra-chart-extension" padded={false}>
                {extension}
              </ContentWrapper>
            </Col>
          )}
        </Row>
        {footer}
      </Spin>
    </Layout>
  );
};

export default Chart;
